import type {
  MapWorkbenchRuntimeFeatureStateHydrationArgs,
  MapWorkbenchRuntimeFeatureStateHydrationResult,
} from '@workspace/ui';

import type { SalesWorkspaceContext } from '@/lib/sales-collaboration';
import { loadSalesLocationSummaries } from '@/lib/sales-location-summaries';

const SALES_LOCATIONS_ALIAS = 'sales_locations';
const SALES_LOCATIONS_LAYER = 'sales_locations';
const PORTAL_DEBUG_LOGS_ENABLED = import.meta.env.DEV && import.meta.env.MODE !== 'test';

const logSalesMapRuntimeFeatureStateDebug = (message: string, details?: Record<string, unknown>) => {
  if (!PORTAL_DEBUG_LOGS_ENABLED || typeof console === 'undefined' || typeof console.debug !== 'function') {
    return;
  }
  if (details) {
    console.debug(`[SalesMap][RuntimeFeatureState] ${message}`, details);
    return;
  }
  console.debug(`[SalesMap][RuntimeFeatureState] ${message}`);
};

const readString = (value: unknown) => {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed || null;
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value);
  }
  return null;
};

const resolveFeatureIdentityKey = (feature: MapWorkbenchRuntimeFeatureStateHydrationArgs['features'][number]) =>
  readString(feature.properties?.feature_identity_key) ?? readString(feature.feature_id);

const resolveLocationId = (feature: MapWorkbenchRuntimeFeatureStateHydrationArgs['features'][number]) =>
  readString(feature.properties?.location_id) ??
  readString(feature.properties?.locationId) ??
  readString(feature.properties?.id);

const hasInterestSignal = (value: unknown) => {
  const normalized = readString(value)?.toLowerCase();
  return normalized === 'interested' || normalized === 'not_interested';
};

const hasCallbackSignal = (value: unknown) => {
  const normalized = readString(value)?.toLowerCase();
  return normalized === 'requested' || normalized === 'completed';
};

const buildNoteColor = (args: {
  doNotContact: boolean;
  followUpRequired: boolean;
  noteCount: number;
}) => {
  if (args.doNotContact) {
    return '#b91c1c';
  }
  if (args.followUpRequired) {
    return '#b45309';
  }
  if (args.noteCount > 0) {
    return '#f47920';
  }
  return null;
};

export async function hydrateSalesMapRuntimeFeatureState(
  args: MapWorkbenchRuntimeFeatureStateHydrationArgs,
  context?: SalesWorkspaceContext | null,
): Promise<MapWorkbenchRuntimeFeatureStateHydrationResult | null> {
  logSalesMapRuntimeFeatureStateDebug('hydrator invoked', {
    sourceAlias: args.target.sourceAlias,
    sourceId: args.target.sourceId,
    sourceLayer: args.target.sourceLayer,
    featureCount: args.features.length,
    featureIds: args.features.map(feature => feature.feature_id),
  });

  if (args.target.sourceAlias !== SALES_LOCATIONS_ALIAS || args.target.sourceLayer !== SALES_LOCATIONS_LAYER) {
    logSalesMapRuntimeFeatureStateDebug('ignoring non-sales-locations target', {
      sourceAlias: args.target.sourceAlias,
      sourceLayer: args.target.sourceLayer,
    });
    return { records: [] };
  }

  if (!args.features.length) {
    logSalesMapRuntimeFeatureStateDebug('no features supplied to hydrator');
    return { records: [] };
  }

  const requestLocations = new Map<string, Record<string, unknown>>();
  for (const feature of args.features) {
    const featureIdentityKey = resolveFeatureIdentityKey(feature);
    if (!featureIdentityKey) {
      logSalesMapRuntimeFeatureStateDebug('skipping feature without feature identity key', {
        featureId: feature.feature_id,
        properties: feature.properties ?? null,
      });
      continue;
    }
    if (requestLocations.has(featureIdentityKey)) {
      continue;
    }
    requestLocations.set(featureIdentityKey, {
      feature_identity_key: featureIdentityKey,
      location_id: resolveLocationId(feature),
      display_name: readString(feature.properties?.display_name),
      address_line1: readString(feature.properties?.address_line1),
      locality: readString(feature.properties?.locality),
      region_code: readString(feature.properties?.region_code),
      postal_code: readString(feature.properties?.postal_code),
      country_code: readString(feature.properties?.country_code),
    });
  }

  logSalesMapRuntimeFeatureStateDebug('built summary request locations', {
    requestedLocationCount: requestLocations.size,
    featureIdentityKeys: Array.from(requestLocations.keys()),
  });

  const summaries = requestLocations.size
    ? await loadSalesLocationSummaries(Array.from(requestLocations.values()) as Array<{
        feature_identity_key: string;
        location_id?: string | null;
        display_name?: string;
        address_line1?: string;
        locality?: string;
        region_code?: string;
        postal_code?: string;
        country_code?: string;
      }>, context, {
        collaboration_signal_only: true,
      })
    : { items: [] };

  logSalesMapRuntimeFeatureStateDebug('received summary response', {
    summaryCount: summaries.items?.length ?? 0,
    summaryFeatureIdentityKeys: (summaries.items ?? []).map(item => item.feature_identity_key),
  });

  const featureByFeatureIdentityKey = new Map(
    args.features
      .map(feature => {
        const featureIdentityKey = resolveFeatureIdentityKey(feature);
        return featureIdentityKey ? [featureIdentityKey, feature] : null;
      })
      .filter((entry): entry is [string, MapWorkbenchRuntimeFeatureStateHydrationArgs['features'][number]] => Boolean(entry)),
  );

  const records = (summaries.items ?? []).flatMap(item => {
    const featureIdentityKey = readString(item.feature_identity_key);
    if (!featureIdentityKey) {
      return [];
    }
    const feature = featureByFeatureIdentityKey.get(featureIdentityKey);
    if (!feature) {
      return [];
    }
    const relationshipSummary = item.location_relationship_summary;
    const noteCount = Math.max(0, Number(relationshipSummary?.note_count ?? 0) || 0);
    const doNotContact = Boolean(relationshipSummary?.do_not_contact);
    const followUpRequired = Boolean(relationshipSummary?.follow_up_required);
    const interestStatus = readString(relationshipSummary?.interest_status);
    const callbackStatus = readString(relationshipSummary?.callback_status);
    const hasCollaborationSignal =
      doNotContact ||
      followUpRequired ||
      noteCount > 0 ||
      hasInterestSignal(interestStatus) ||
      hasCallbackSignal(callbackStatus);
    if (!hasCollaborationSignal) {
      return [];
    }

    return [{
      source_id: args.target.sourceId,
      source_layer: args.target.sourceLayer,
      feature_id: feature.feature_id,
      state: {
        has_collaboration_signal: true,
        has_notes: noteCount > 0,
        note_count: noteCount,
        do_not_contact: doNotContact,
        follow_up_required: followUpRequired,
        ...(interestStatus ? { interest_status: interestStatus } : {}),
        ...(callbackStatus ? { callback_status: callbackStatus } : {}),
        note_color: buildNoteColor({ doNotContact, followUpRequired, noteCount }),
      },
    }];
  });

  logSalesMapRuntimeFeatureStateDebug('returning feature-state records', {
    recordCount: records.length,
    records: records.map(record => ({
      featureId: record.feature_id,
      state: record.state,
    })),
  });

  return { records };
}
